import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { AutoAwesome, DeleteOutline } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { LockedFeature } from './planLock/LockedFeature';
import { usePlanSurface } from './planLock/usePlanSurface';
import { useActingStoreAdmin } from '../hooks/useActingStoreAdmin';
import { useToast } from '../contexts/ToastContext';
import { useUnsavedChangesSource } from './appUpdate/unsavedChanges';
import { ChipInput, InlineMessage, LoadingState, SaveBar, SectionCard, UsageBar } from './ui';
import {
  STORE_AI_COPY_RULES_KEY,
  StoreAiCopyBrandNote,
  StoreAiCopyLength,
  StoreAiCopyRules,
  fetchStoreAiCopyRules,
  saveStoreAiCopyRules,
} from '../services/storeAiCopyApi';
import { TrialFeatureTitle } from './trial/TrialFeatureBadge';

const LENGTHS: StoreAiCopyLength[] = ['short', 'medium', 'long'];
const MAX_BRAND_NOTES = 20;

interface RulesForm {
  voice: string;
  titlePattern: string;
  bannedPhrases: string[];
  descriptionLength: StoreAiCopyLength | '';
  brandNotes: StoreAiCopyBrandNote[];
}

const toForm = (rules: StoreAiCopyRules | undefined): RulesForm => ({
  voice: rules?.voice || '',
  titlePattern: rules?.titlePattern || '',
  bannedPhrases: rules?.bannedPhrases || [],
  descriptionLength: rules?.descriptionLength || '',
  brandNotes: (rules?.brandNotes || []).map((n) => ({ brand: n.brand, note: n.note })),
});

/** What the server stores: trimmed, empties dropped. Brand notes missing either half are left out. */
const toRules = (form: RulesForm): StoreAiCopyRules => ({
  voice: form.voice.trim(),
  titlePattern: form.titlePattern.trim(),
  bannedPhrases: form.bannedPhrases.map((p) => p.trim()).filter(Boolean),
  ...(form.descriptionLength ? { descriptionLength: form.descriptionLength } : {}),
  brandNotes: form.brandNotes
    .map((n) => ({ brand: n.brand.trim(), note: n.note.trim() }))
    .filter((n) => n.brand && n.note),
});

const sameRules = (a: StoreAiCopyRules, b: StoreAiCopyRules): boolean =>
  JSON.stringify({ ...a, descriptionLength: a.descriptionLength || '' }) ===
  JSON.stringify({ ...b, descriptionLength: b.descriptionLength || '' });

/**
 * Jordan's rewrite rules for the store's own AI titles and descriptions
 * (flag `store_ai_copy`, MOT-153 / estimate row B20): voice, title pattern,
 * banned phrases, length and per-brand notes, with this month's generation
 * budget. Hidden with the flag off; shows the plan state when the plan does
 * not include AI content tools. Edits collect locally and save from the bar.
 */
export const StoreAiCopyRulesCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('store_ai_copy');
  const surface = usePlanSurface({ feature: 'aiContentTools', orgFlag: 'store_ai_copy' });
  // The routes write for an owner or admin (or a platform admin viewing as the store); everyone else reads.
  const { canManage: canEdit } = useActingStoreAdmin();
  const [form, setForm] = useState<RulesForm>(toForm(undefined));

  const { data, isLoading, isError } = useQuery({
    queryKey: STORE_AI_COPY_RULES_KEY,
    queryFn: () => fetchStoreAiCopyRules(t('store.aiCopy.loadError')),
    enabled: enabled && surface.mode === 'open',
  });

  useEffect(() => {
    if (data) setForm(toForm(data.rules));
  }, [data]);

  const saved = useMemo(() => toRules(toForm(data?.rules)), [data?.rules]);
  const current = useMemo(() => toRules(form), [form]);
  const dirty = canEdit && Boolean(data) && !sameRules(saved, current);

  const save = useMutation({
    mutationFn: () => saveStoreAiCopyRules(current, t('store.aiCopy.saveError')),
    onSuccess: () => {
      showToast(t('store.aiCopy.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: STORE_AI_COPY_RULES_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const discard = () => setForm(toForm(data?.rules));
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  if (surface.mode === 'locked') {
    return (
      <SectionCard title={t('store.aiCopy.title')} leading={<AutoAwesome color="primary" />}>
        <LockedFeature variant="inline" planName={surface.planName} title={t('store.aiCopy.title')} />
      </SectionCard>
    );
  }
  if (!enabled || surface.mode !== 'open') return null;

  const setField = <K extends keyof RulesForm>(key: K, value: RulesForm[K]) => setForm((prev) => ({ ...prev, [key]: value }));
  const setBrandNote = (index: number, patch: Partial<StoreAiCopyBrandNote>) =>
    setForm((prev) => ({ ...prev, brandNotes: prev.brandNotes.map((n, i) => (i === index ? { ...n, ...patch } : n)) }));

  return (
    <SectionCard title={<TrialFeatureTitle title={t('store.aiCopy.title')} feature="aiContentTools" />} leading={<AutoAwesome color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('store.aiCopy.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.aiCopy.intro')}
          </Typography>
          {!data.planIncluded && <InlineMessage severity="info">{t('store.aiCopy.planLocked')}</InlineMessage>}
          <UsageBar
            label={t('store.aiCopy.usageLabel')}
            used={data.usage.used}
            limit={data.usage.limit === -1 ? null : data.usage.limit}
            unit={t('store.aiCopy.usageUnit')}
          />
          <TextField
            label={t('store.aiCopy.voice')}
            value={form.voice}
            onChange={(event) => setField('voice', event.target.value)}
            multiline
            minRows={2}
            size="small"
            disabled={!canEdit}
            inputProps={{ maxLength: 600 }}
            helperText={t('store.aiCopy.voiceHelp')}
          />
          <TextField
            label={t('store.aiCopy.titlePattern')}
            value={form.titlePattern}
            onChange={(event) => setField('titlePattern', event.target.value)}
            size="small"
            disabled={!canEdit}
            inputProps={{ maxLength: 160 }}
            placeholder="{brand} {model} {name}"
            helperText={t('store.aiCopy.titlePatternHelp')}
          />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('store.aiCopy.bannedPhrases')}
            </Typography>
            <ChipInput
              aria-label={t('store.aiCopy.bannedPhrases')}
              value={form.bannedPhrases}
              onChange={(value) => setField('bannedPhrases', value.slice(0, 50))}
              placeholder={t('store.aiCopy.bannedPhrasesPlaceholder')}
              disabled={!canEdit}
            />
            <Typography variant="caption" color="text.secondary">
              {t('store.aiCopy.bannedPhrasesHelp')}
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            label={t('store.aiCopy.length')}
            value={form.descriptionLength}
            onChange={(event) => setField('descriptionLength', event.target.value as StoreAiCopyLength | '')}
            disabled={!canEdit}
            sx={{ maxWidth: 360 }}
          >
            <MenuItem value="">{t('store.aiCopy.lengthDefault')}</MenuItem>
            {LENGTHS.map((length) => (
              <MenuItem key={length} value={length}>
                {t(`store.aiCopy.lengths.${length}`)}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('store.aiCopy.brandNotes')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('store.aiCopy.brandNotesHelp')}
            </Typography>
            <Stack spacing={1}>
              {form.brandNotes.map((note, index) => (
                <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    size="small"
                    label={t('store.aiCopy.brand')}
                    value={note.brand}
                    onChange={(event) => setBrandNote(index, { brand: event.target.value })}
                    disabled={!canEdit}
                    inputProps={{ maxLength: 80 }}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    label={t('store.aiCopy.brandNote')}
                    value={note.note}
                    onChange={(event) => setBrandNote(index, { note: event.target.value })}
                    disabled={!canEdit}
                    inputProps={{ maxLength: 300 }}
                    fullWidth
                  />
                  <IconButton
                    aria-label={t('store.aiCopy.removeBrandNote')}
                    size="small"
                    disabled={!canEdit}
                    onClick={() => setField('brandNotes', form.brandNotes.filter((_, i) => i !== index))}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canEdit || form.brandNotes.length >= MAX_BRAND_NOTES}
                  onClick={() => setField('brandNotes', [...form.brandNotes, { brand: '', note: '' }])}
                >
                  {t('store.aiCopy.addBrandNote')}
                </Button>
              </Box>
            </Stack>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t(canEdit ? 'store.aiCopy.ownerOnly' : 'store.aiCopy.readOnly')}
          </Typography>
          {canEdit && (
          <SaveBar dirty={dirty} message={t('store.aiCopy.saveMessage')} saving={save.isPending} onSave={() => save.mutate()} onDiscard={discard} />
          )}
        </Stack>
      )}
    </SectionCard>
  );
};

export default StoreAiCopyRulesCard;
